import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * The shared bottom-sheet frame (SNIPPETS §4) over the existing `.sheet` overlay: a grab handle,
 * a title with an optional line under it, the body, and a footer slot for an in-sheet ActionBar
 * (`<ActionBar inSheet … />`). Closes on the scrim and on Escape; rises at 220 ms.
 *
 * When to use: any form of 1–4 fields, a picker, a confirm. Opt-in — existing sheets that pass
 * their own `.sheet > .pane` markup keep rendering as they are. Render it only while open.
 *
 * Portalled to `<body>`: rendered inside a page, `.ion-page`'s `contain: layout` made the page the
 * overlay's containing block, so the tab bar and LiveBar sat on top of the sheet.
 */
export function Sheet({
  title,
  sub,
  onClose,
  children,
  footer,
  large,
}: {
  title: ReactNode;
  /** 13.8 muted under the title: "Wed 23 Sep · 07:12" */
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** set at the question screens' large scale (kit §28) — a portal does not inherit the page's */
  large?: boolean;
}) {
  const titleId = useId();
  const pane = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Only the sheet on top answers: the Add-a-food sheet opens over the picker, and one Escape
      // closing both lost the search along with the form.
      const panes = document.querySelectorAll(".k-sheet__pane");
      if (e.key === "Escape" && panes[panes.length - 1] === pane.current) close.current();
    };
    document.addEventListener("keydown", onKey);
    // Focus lands in the sheet so a keyboard or screen-reader user starts where the content is.
    pane.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return createPortal(
    <div className={`sheet k-sheet${large ? " k-large" : ""}`} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={pane}
        className="k-sheet__pane"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <span className="k-sheet__grab" aria-hidden="true" />
        <div className="k-sheet__head">
          <h2 id={titleId} className="k-sheet__title">
            {title}
          </h2>
          {sub ? <p className="k-sheet__sub">{sub}</p> : null}
        </div>
        <div className="k-sheet__body">{children}</div>
        {footer}
      </div>
    </div>,
    document.body
  );
}
